import { Icon, List, ListItem, ListItemSecondaryAction, IconButton, ListItemText, Chip, Tooltip, FormHelperText } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Spin from 'components/Spin';
import { useFormikContext, getIn } from 'formik';
import React, { useState, useCallback, useEffect } from 'react';
import ReactDropzone from 'react-dropzone';

import useStyle from './styles';

import type { IValues } from './index';

const PIN_SIZE = 60;

interface IFilePins {
  name: string;
  size: number;
  type: string;
  pins: string[];
  duplicatedPins?: number;
}

export function DropZone() {
  const classes = useStyle();
  const [loading] = useState<boolean>(false);
  const [files, setFiles] = useState<IFilePins[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);
  const [pins, setPins] = useState<string[]>([]);
  const { errors, dirty, setFieldValue } = useFormikContext<IValues>();
  const errorText = getIn(errors, 'pins');

  useEffect(() => {
    setFieldValue('pins', pins);
  }, [pins, setFieldValue]);

  const readFile = (file: File): Promise<IFilePins> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const content = fileReader.result;
        const pinsArray: string[] = content.toString().match(/[^\r\n]+/g);
        let pinsNoSpaceBetween = pinsArray.map((pin) => pin.replace(/\s/g, ''));
        const isCSV = file.type === 'application/vnd.ms-excel' || file.name.search(/\.csv+$/i) !== -1;
        if (isCSV) {
          const cleanEmptyPinsAndSemiColon = (pins: string[]) => pins.filter((pin) => pin !== ';').map((pin) => pin.replace(/;/g, ''));
          pinsNoSpaceBetween = cleanEmptyPinsAndSemiColon(pinsNoSpaceBetween);
        }
        const uniquePins = new Set(pinsNoSpaceBetween.filter((i) => i.length <= PIN_SIZE));
        const filePins = Array.from(uniquePins);
        resolve({ name: file.name, size: file.size, type: file.type, pins: filePins });
      };

      fileReader.onerror = () => {
        reject(fileReader);
      };
      fileReader.readAsText(file);
    });
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: File[]) => {
      let allPins: string[] = pins;

      setRejectedFiles(rejectedFiles);

      let filesToUpload: Promise<IFilePins>[] = [];

      acceptedFiles.forEach((f) => filesToUpload.push(readFile(f)));

      Promise.all(filesToUpload)
        .then((uploadedFiles) => {
          uploadedFiles.forEach((u) => {
            let nFile: IFilePins = u;
            const fileExist = files.some((f) => f.name === nFile.name);
            if (fileExist) return;
            nFile.duplicatedPins = nFile.pins.filter((p) => allPins.includes(p)).length;
            nFile.pins = nFile.pins.filter((p) => !allPins.includes(p));
            setFiles((prev) => prev.concat(nFile));
            allPins = allPins.concat(nFile.pins);
          });
        })
        .finally(() => setPins(allPins));
    },
    [files, pins]
  );

  const removeFile = useCallback(
    (file: IFilePins) => {
      setPins((prev) => prev.filter((f) => !file.pins.includes(f)));
      setFiles(files.filter((f) => f.name !== file.name));
    },
    [files]
  );

  if (loading) {
    return (
      <div>
        <Spin />
        <p>Por favor aguarde, estamos fazendo upload do(s) arquivo(s) ...</p>
      </div>
    );
  }

  const qtdRejected = rejectedFiles?.length || 0;
  return (
    <>
      {qtdRejected ? (
        <Alert severity='warning' className={classes.alert}>
          <p>
            Atenção,
            <Tooltip
              title={
                <>
                  <strong>Arquivos</strong>
                  {rejectedFiles.map((r) => (
                    <p key={r.name}>{r.name}</p>
                  ))}
                </>
              }
              className={classes.linkTooltip}
              classes={{ tooltip: classes.tooltip }}
            >
              <strong>
                <a>
                  {qtdRejected} {qtdRejected > 1 ? 'arquivos' : 'arquivo'}
                </a>
              </strong>
            </Tooltip>
            {qtdRejected > 1 ? 'estão' : ' está'} com formato ou tamanho incorreto, por favor siga as instruções acima.
          </p>
        </Alert>
      ) : null}
      <ReactDropzone onDrop={onDrop} accept={['text/plain', '.csv', 'application/vnd.ms-excel']} maxSize={8000000}>
        {({ getRootProps, getInputProps }) => (
          <section>
            <div className={classes.zone} {...getRootProps()}>
              <input {...getInputProps()} />
              <Icon>backup</Icon>
              <p>Clique ou arraste os arquivos nessa área</p>
              <p>Pode ser selecionado vários arquivos ao mesmo tempo!</p>
            </div>
          </section>
        )}
      </ReactDropzone>
      {errorText && dirty && <FormHelperText style={{ color: '#df4d60' }}>{errorText}</FormHelperText>}
      <List className={classes.list}>
        {!!files?.length && files.map((item: IFilePins) => <ListItemDescription key={item.name} item={item} removeFile={removeFile} />)}
      </List>
      {!!pins.length && <strong>Total de pins: {pins.length}</strong>}
    </>
  );
}

const ListItemDescription = ({ item, removeFile }: { item: IFilePins; removeFile: (file: IFilePins) => void }) => {
  const classes = useStyle();
  const bytesToSize = useCallback((bytes: any) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  const msg = !!item.duplicatedPins
    ? `O arquivo ${item.name} contém ${item.pins.length} pins
    e ${item.duplicatedPins} pins já cadastrados em outro arquivo.`
    : `O arquivo ${item.name} contém ${item.pins.length} pins`;

  return (
    <ListItem className={classes.file} key={`file-${item.name}`}>
      <ListItemText primary={<strong>{item.name}</strong>} secondary={`Tamanho: ${bytesToSize(item.size)}`}></ListItemText>
      <Tooltip title={msg}>
        <Chip label={`${item.pins?.length || 0} Pins`} style={{ marginRight: 20, color: 'black', fontWeight: 500 }} variant='outlined'></Chip>
      </Tooltip>
      <ListItemSecondaryAction>
        <IconButton onClick={() => removeFile(item)}>
          <Icon>clear</Icon>
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};
